import { z } from "zod";
import {
	EVENT_BOOKING_SCOPE,
	EVENT_GROUP_BOOKING_MODE,
} from "../constants/event.constants";
import { eventCategoryIdSchema } from "./eventCategory.schemas";

export const eventOccasionIdSchema = z.coerce.number().int().positive();

// Accept common boolean shapes from clients: true/false, 0/1, "true"/"false", "0"/"1"
const booleanish = z
	.union([
		z.boolean(),
		z.literal(0),
		z.literal(1),
		z.literal("0"),
		z.literal("1"),
		z.literal("true"),
		z.literal("false"),
	])
	.transform((val) => {
		if (typeof val === "boolean") return val;
		const s = String(val).toLowerCase();
		return s === "1" || s === "true";
	});

export const eventOccasionSchema = z
	.object({
		eventOccasionId: eventOccasionIdSchema.optional(),
		tenantId: z.coerce.number().int().positive(),
		eventCategoryId: eventCategoryIdSchema,
		title: z.string().min(1).max(255),
		subtitle: z.string().max(255).nullable(),
		description: z.string().max(4000).nullable(),
		/**
		 * Hero image URL - accepts relative paths (e.g., "/uploads/image.jpg"),
		 * absolute URLs, or data URLs. Using z.string() instead of z.string().url()
		 * to support local file storage paths and uploaded image references.
		 */
		heroImageUrl: z.string().nullable(),
		/**
		 * Gallery URLs - accepts relative paths, absolute URLs, or data URLs.
		 * Using z.string() instead of z.string().url() to support local file
		 * storage paths and uploaded image references.
		 */
		gallery: z.array(z.string()).default([]),
		tags: z.array(z.string().min(1).max(64)).default([]),
		membershipScope: z.enum(EVENT_BOOKING_SCOPE).default("member_only"),
		allowNonMember: z.boolean().default(false),
		isPaidEvent: z.boolean().default(false),
		price: z.union([z.coerce.number().nonnegative(), z.null()]).optional(),
		currency: z.string().length(3).optional(),
		groupBookingMode: z.enum(EVENT_GROUP_BOOKING_MODE).default("single"),
		allowMultipleBookings: z.boolean().default(false),
		reservationStartAt: z.coerce.date().nullable(),
		reservationEndAt: z.coerce.date().nullable(),
		reminderTemplateId: z.coerce.number().int().positive().nullable(),
		cancellationPolicyId: z.coerce.number().int().positive().nullable(),
		isPublished: z.boolean().default(false),
		publishedAt: z.coerce.date().nullable(),
		displayOrder: z.coerce.number().int().min(0).default(0),
		linePostId: z.string().nullable(),
		isDisplayed: z.boolean().default(true),
		fee: z.coerce.number().nonnegative().default(0),
		cancelable: z.boolean().default(false),
		cancelConditions: z
			.array(
				z.object({
					name: z.string().optional(),
					day: z.coerce.number().int().min(0),
					hour: z.coerce.number().int().min(0).max(23),
					minute: z.coerce.number().int().min(0).max(59),
					refundPercentage: z.coerce.number().int().min(0).max(100).optional(),
				}),
			)
			.default([])
			.optional(),
		cancelDescription: z.string().max(1000).nullable().optional(),
		isMessage: z.boolean().default(false),
		isMultiEvent: z.boolean().default(true),
		notRegisterEventSameTime: z.boolean().default(false),
		isProgram: z.boolean().default(false),
		messageRegisteredLineWorks: z.string().max(500).nullable().optional(),
		messageCanceledLineWorks: z.string().max(500).nullable().optional(),
		adminNotificationMemberIds: z
			.array(z.number().int())
			.default([])
			.optional(),
		categoryMessageDetail: z.unknown().optional(),
		totalMaxCapacity: z.coerce.number().int().optional(),
		totalReservations: z.coerce.number().int().optional(),
		earliestOccurrenceStart: z.coerce.date().nullable().optional(),
		latestOccurrenceEnd: z.coerce.date().nullable().optional(),
		createdAt: z.coerce.date().optional(),
		updatedAt: z.coerce.date().optional(),
		deletedAt: z.coerce.date().nullable().optional(),
	})
	.strict();

export const createEventOccasionSchema = eventOccasionSchema
	.omit({
		eventOccasionId: true,
		createdAt: true,
		updatedAt: true,
		deletedAt: true,
		publishedAt: true,
	})
	.extend({
		tenantId: z.coerce.number().int().positive().optional(),
		subtitle: z.string().max(255).optional().nullable(),
		description: z.string().max(4000).optional().nullable(),
		heroImageUrl: z.string().optional().nullable(),
		gallery: z.array(z.string()).optional(),
		tags: z.array(z.string().min(1).max(64)).optional(),
		membershipScope: z.enum(EVENT_BOOKING_SCOPE).optional(),
		allowNonMember: booleanish.optional(),
		isPaidEvent: booleanish.optional(),
		price: z.union([z.coerce.number().nonnegative(), z.null()]).optional(),
		currency: z.string().length(3).optional(),
		groupBookingMode: z.enum(EVENT_GROUP_BOOKING_MODE).optional(),
		allowMultipleBookings: booleanish.optional(),
		reservationStartAt: z.coerce.date().optional().nullable(),
		reservationEndAt: z.coerce.date().optional().nullable(),
		reminderTemplateId: z.coerce
			.number()
			.int()
			.positive()
			.optional()
			.nullable(),
		cancellationPolicyId: z.coerce
			.number()
			.int()
			.positive()
			.optional()
			.nullable(),
		isPublished: booleanish.optional(),
		displayOrder: z.coerce.number().int().min(0).optional(),
		linePostId: z.string().optional().nullable(),
		isDisplayed: booleanish.optional(),
		fee: z.coerce.number().nonnegative().optional(),
		cancelable: booleanish.optional(),
		cancelConditions: z.array(z.any()).optional(),
		cancelDescription: z.string().max(1000).optional().nullable(),
		isMessage: booleanish.optional(),
		isMultiEvent: booleanish.optional(),
		notRegisterEventSameTime: booleanish.optional(),
		isProgram: booleanish.optional(),
		messageRegisteredLineWorks: z.string().max(500).optional().nullable(),
		messageCanceledLineWorks: z.string().max(500).optional().nullable(),
		adminNotificationMemberIds: z.array(z.number().int()).optional(),
		categoryMessageDetail: z.unknown().optional(),
		// Accept publishedAt on create but do not require it; service ignores this field on insert
		publishedAt: z.coerce.date().optional().nullable(),
	});

export const updateEventOccasionSchema = createEventOccasionSchema.partial({
	title: true,
	subtitle: true,
	description: true,
	heroImageUrl: true,
	gallery: true,
	tags: true,
	membershipScope: true,
	allowNonMember: true,
	isPaidEvent: true,
	price: true,
	currency: true,
	groupBookingMode: true,
	allowMultipleBookings: true,
	reservationStartAt: true,
	reservationEndAt: true,
	reminderTemplateId: true,
	cancellationPolicyId: true,
	isPublished: true,
	publishedAt: true,
	displayOrder: true,
	linePostId: true,
	isDisplayed: true,
	fee: true,
	cancelable: true,
	cancelConditions: true,
	cancelDescription: true,
	isMessage: true,
	isMultiEvent: true,
	notRegisterEventSameTime: true,
	isProgram: true,
	messageRegisteredLineWorks: true,
	messageCanceledLineWorks: true,
	adminNotificationMemberIds: true,
	categoryMessageDetail: true,
});
